import { DataGrid } from '@mui/x-data-grid';
import { ukUA, enUS } from '@mui/x-data-grid/locales';
import { useTranslation } from 'react-i18next';

const dataGridStyles = {
    width: '100%',
    backgroundColor: 'var(--box-background-color)',
    color: 'var(--text-color)',
    border: 'none',
    '& .MuiDataGrid-root': {
        border: 'none',
        backgroundColor: 'var(--box-background-color)',
        color: 'var(--text-color)',
        width: '100%',
    },
    '& .MuiDataGrid-main': {
        backgroundColor: 'var(--box-background-color)',
        width: '100%',
    },
    '& .MuiDataGrid-virtualScroller': {
        backgroundColor: 'var(--box-background-color)',
    },
    '& .MuiDataGrid-container--top [role=row]': {
        backgroundColor: 'var(--box-background-color)',
    },
    '& .MuiDataGrid-container--bottom [role=row]': {
        backgroundColor: 'var(--box-background-color)',
    },
    '& .MuiDataGrid-cell': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        color: 'var(--text-color)',
        backgroundColor: 'var(--box-background-color)',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        color: 'var(--text-color)',
        fontWeight: 600,
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
        color: 'var(--text-color)',
        '&:focus': {
            backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
        },
        '&:focus-within': {
            backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
        },
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: 'var(--text-color)',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer': {
        backgroundColor: 'transparent',
    },
    '& .MuiDataGrid-columnSeparator': {
        color: 'rgba(255, 255, 255, 0.1)',
    },
    '& .MuiDataGrid-row': {
        backgroundColor: 'var(--box-background-color)',
        color: 'var(--text-color)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
        '&.Mui-selected': {
            backgroundColor: 'rgba(84, 112, 153, 0.2)',
            '&:hover': {
                backgroundColor: 'rgba(84, 112, 153, 0.3)',
            },
        },
    },
    '& .MuiDataGrid-footerContainer': {
        borderColor: 'rgba(255, 255, 255, 0.1)',
        color: 'var(--text-color)',
        backgroundColor: 'var(--box-background-color)',
    },
    '& .MuiTablePagination-root': {
        color: 'var(--text-color)',
    },
    '& .MuiTablePagination-selectLabel': {
        color: 'var(--text-color)',
    },
    '& .MuiTablePagination-displayedRows': {
        color: 'var(--text-color)',
    },
    '& .MuiTablePagination-select': {
        color: 'var(--text-color)',
        backgroundColor: 'var(--box-background-color)',
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.2)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--button-primary)',
        },
    },
    '& .MuiSelect-icon': {
        color: 'var(--text-color)',
    },
    '& .MuiIconButton-root': {
        color: 'var(--text-color)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
    },
    '& .MuiDataGrid-loadingOverlay': {
        backgroundColor: 'rgba(26, 29, 31, 0.8)',
    },
    '& .MuiCircularProgress-root': {
        color: 'var(--button-primary)',
    },
    '& .MuiDataGrid-overlayWrapper': {
        backgroundColor: 'var(--box-background-color)',
    },
    '& .MuiDataGrid-overlay': {
        backgroundColor: 'var(--box-background-color)',
    },
    // Time-status row classes (set via getRowClassName)
    '& .MuiDataGrid-row.row-active': {
        borderLeft: '3px solid var(--button-primary)',
        '& .MuiDataGrid-cell': {
            backgroundColor: 'rgba(84, 112, 153, 0.08)',
        },
        '&:hover .MuiDataGrid-cell': {
            backgroundColor: 'rgba(84, 112, 153, 0.14)',
        },
    },
    '& .MuiDataGrid-row.row-past': {
        '& .MuiDataGrid-cell': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            color: 'rgba(255, 255, 255, 0.38)',
        },
        '&:hover .MuiDataGrid-cell': {
            backgroundColor: 'rgba(0, 0, 0, 0.16)',
            color: 'rgba(255, 255, 255, 0.44)',
        },
    },
    '& .MuiDataGrid-scrollbar': {
        '& .MuiDataGrid-scrollbar--vertical': {
            backgroundColor: 'var(--box-background-color)',
        },
        '& .MuiDataGrid-scrollbar--horizontal': {
            backgroundColor: 'var(--box-background-color)',
        },
    },
    '& .MuiDataGrid-filler': {
        backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
    },
};

const localeTextMap = {
    uk: ukUA.components.MuiDataGrid.defaultProps.localeText,
    en: enUS.components.MuiDataGrid.defaultProps.localeText,
};

function TableGrid({
    noRowsText,
    columns,
    rows,
    loading,
    rowCount,
    paginationMode = 'client',
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions = [5, 10, 25, 50],
    sortingMode = 'client',
    disableRowSelectionOnClick = true,
    disableColumnMenu = true,
    disableColumnSort = true,
    ...props
}) {
    const { i18n } = useTranslation();
    const localeText = localeTextMap[i18n.resolvedLanguage] ?? localeTextMap.uk;

    const a = {
        ...localeText,
        paginationDisplayedRows: ({ from, to, count }) =>
            `${from}–${to} із ${count !== -1 ? count : `більше ніж ${to}`}`,
    }

    return (
        <DataGrid
            localeText={a}
            columns={columns}
            rows={rows}
            loading={loading}
            rowCount={rowCount}
            paginationMode={paginationMode}
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            pageSizeOptions={pageSizeOptions}
            sortingMode={sortingMode}
            disableRowSelectionOnClick={disableRowSelectionOnClick}
            disableColumnMenu={disableColumnMenu}
            disableColumnSort={disableColumnSort}
            sx={dataGridStyles}
            {...props}
        />
    );
}

export default TableGrid;
